<script setup lang="ts">
import { Icon } from '@iconify/vue'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import type { BatchRoll } from '@/types/batch-types'
import { unitOptions } from '@/constants/dimension'
import FormInputText from '@/components/inputs/FormInputText.vue'
import FormDropdown from '@/components/inputs/FormDropdown.vue'
import { computed, ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { navigateToBatch, navigateToBatchByRollId } from '@/utils/batch-navigation'
import { findReturnedBatchByRollId } from '@/api/batches'

const props = withDefaults(defineProps<{
  editable?: boolean
  showActions?: boolean
  currentBatchNumber?: string
}>(), {
  editable: false,
  showActions: true
})

const router = useRouter()

const emit = defineEmits<{
  remove: [value: BatchRoll]
}>()

const model = defineModel<BatchRoll>('modelValue', {
  required: true
})

const isNavigatingToOriginal = ref(false)
const isNavigatingToCurrent = ref(false)
const actualCurrentBatchNumber = ref<string | null>(null)
const actualCurrentBatchId = ref<string | null>(null)

const remove = () => emit('remove', model.value)

const id = (propertyName: string) => `roll-${model.value.id}-${propertyName}`

const rollStateInfo = computed(() => {
  switch (model.value.rollState) {
    case 0:
      return { label: 'Greig', severity: 'info', icon: 'mdi:home' }
    case 1:
      return { label: 'Offsite', severity: 'warning', icon: 'mdi:truck' }
    case 2:
      return { label: 'Finished', severity: 'success', icon: 'mdi:check-circle' }
    case 3:
      return { label: 'Available', severity: 'success', icon: 'mdi:package-variant' }
    case 4:
      return { label: 'Dispatched', severity: 'secondary', icon: 'mdi:send' }
    default:
      return { label: 'Unknown', severity: 'secondary', icon: 'mdi:help-circle' }
  }
})

const shouldShowOriginalBatch = computed(() => {
  return model.value.originalBatchNumber &&
      model.value.originalBatchNumber !== props.currentBatchNumber
})

const shouldShowCurrentBatch = computed(() => {
  const currentBatch = actualCurrentBatchNumber.value || model.value.currentBatchNumber
  return currentBatch && currentBatch !== props.currentBatchNumber
})

const displayCurrentBatchNumber = computed(() => {
  return actualCurrentBatchNumber.value || model.value.currentBatchNumber
})

const fetchActualCurrentBatch = async () => {
  if (model.value.returnedRollId && model.value.returnedRollId !== "00000000-0000-0000-0000-000000000000") {
    try {
      const returnedBatch = await findReturnedBatchByRollId(model.value.returnedRollId)
      if (returnedBatch) {
        actualCurrentBatchNumber.value = returnedBatch.batchNumber
        actualCurrentBatchId.value = returnedBatch.id || null
      }
    } catch (error) {
      console.error('Error fetching actual current batch:', error)
    }
  }
}

const navigateToOriginalBatch = (event: Event) => {
  event.stopPropagation()
  event.preventDefault()

  if (isNavigatingToOriginal.value) return

  if (model.value.id) {
    isNavigatingToOriginal.value = true

    setTimeout(async () => {
      try {
        await navigateToBatchByRollId(router, model.value.id as string)
      } catch (error) {
        console.error('Navigation failed:', error)
      } finally {
        isNavigatingToOriginal.value = false
      }
    }, 0)
  } else {
    console.warn('No roll ID found')
  }
}

const navigateToCurrentBatch = (event: Event) => {
  event.stopPropagation()
  event.preventDefault()

  if (isNavigatingToCurrent.value) return

  const targetBatchId = actualCurrentBatchId.value || model.value.batchId

  if (targetBatchId) {
    isNavigatingToCurrent.value = true

    setTimeout(async () => {
      try {
        await navigateToBatch(router, targetBatchId as string)
      } catch (error) {
        console.error('Navigation failed:', error)
      } finally {
        isNavigatingToCurrent.value = false
      }
    }, 0)
  } else {
    console.warn('No batchId found')
  }
}

watch(() => model.value.returnedRollId, () => {
  fetchActualCurrentBatch()
}, { immediate: true })

onMounted(() => {
  fetchActualCurrentBatch()
})
</script>

<template>
  <div class="grid grid-cols-12 gap-x-10">
    <div class="col-span-2">
      <label :for="id('rollNo')" class="text-xs">Roll No.</label>
      <InputText
        v-model="model.rollNumber"
        :disabled="!editable"
        :id="id('rollNo')"
        class="w-full text-sm h-8"
        input-class="max-w-full"
      />
    </div>
    <div class="col-span-2">
      <label :for="id('lengthValue')" class="text-xs">Length</label>
      <InputNumber
        v-model="model.lengthValue"
        :disabled="!editable"
        :id="id('lengthValue')"
        class="w-full text-sm h-8"
        input-class=" max-w-full"
      />
    </div>
    <div class="col-span-2">
      <label :for="id('lengthUnit')" class="text-xs">Unit</label>
      <Dropdown
        label="Unit"
        v-model="model.lengthUnit"
        :disabled="!editable"
        class="w-full text-sm h-8"
        inputName="lengthUnit"
        id="lengthUnit"
        :options="unitOptions"
        optionLabel="label"
        optionValue="value"
        type="number"
      />
    </div>
    <div class="col-span-2">
      <label :for="id('weight')" class="text-xs">Weight</label>
      <InputNumber
        v-model="model.weight"
        :disabled="!editable"
        :id="id('weight')"
        class="w-full text-sm h-8"
        input-class="max-w-full"
      />
    </div>

    <div class="col-span-4 flex items-end justify-between" v-if="showActions">
      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-if="model.isActive && editable"
          @click="remove"
          size="small"
          outlined
          severity="danger"
        >
          Remove
          <Icon icon="ep:remove-filled" class="ml-3" />
        </Button>

        <div class="flex flex-col gap-1">

          <Tag :severity="rollStateInfo.severity" class="!px-2 !py-1 text-xs">
            <Icon :icon="rollStateInfo.icon" class="text-sm me-1" />
            {{ rollStateInfo.label }}
          </Tag>

          <Tag
              v-if="shouldShowOriginalBatch"
              severity="secondary"
              :class="[
                '!px-2 !py-1 text-xs cursor-pointer transition-colors select-none',
                isNavigatingToOriginal
                  ? 'opacity-50 cursor-wait'
                  : 'hover:bg-gray-200'
              ]"
              @click.stop.prevent="navigateToOriginalBatch"
              @mousedown.stop
              @mouseup.stop
          >
            <Icon
                :icon="isNavigatingToOriginal ? 'mdi:loading' : 'mdi:package-variant-closed'"
                :class="['text-sm me-1', isNavigatingToOriginal ? 'animate-spin' : '']"
            />
            Origin: {{ model.originalBatchNumber }}
          </Tag>

          <Tag
              v-if="shouldShowCurrentBatch"
              severity="info"
              :class="[
                '!px-2 !py-1 text-xs cursor-pointer transition-colors select-none',
                isNavigatingToCurrent
                  ? 'opacity-50 cursor-wait'
                  : 'hover:bg-blue-200'
              ]"
              @click.stop.prevent="navigateToCurrentBatch"
              @mousedown.stop
              @mouseup.stop
          >
            <Icon
                :icon="isNavigatingToCurrent ? 'mdi:loading' : 'mdi:package-variant'"
                :class="['text-sm me-1', isNavigatingToCurrent ? 'animate-spin' : '']"
            />
            Current: {{ displayCurrentBatchNumber }}
          </Tag>
        </div>
      </div>
    </div>

    <div class="col-span-4 flex items-end" v-else>
      <div class="flex flex-col gap-1">
        <Tag :severity="rollStateInfo.severity" class="!px-2 !py-1 text-xs">
          <Icon :icon="rollStateInfo.icon" class="text-sm me-1" />
          {{ rollStateInfo.label }}
        </Tag>

        <Tag
            v-if="shouldShowOriginalBatch"
            severity="secondary"
            :class="[
              '!px-2 !py-1 text-xs cursor-pointer transition-colors select-none',
              isNavigatingToOriginal
                ? 'opacity-50 cursor-wait'
                : 'hover:bg-gray-200'
            ]"
            @click.stop.prevent="navigateToOriginalBatch"
            @mousedown.stop
            @mouseup.stop
        >
          <Icon
              :icon="isNavigatingToOriginal ? 'mdi:loading' : 'mdi:package-variant-closed'"
              :class="['text-sm me-1', isNavigatingToOriginal ? 'animate-spin' : '']"
          />
          Origin: {{ model.originalBatchNumber }}
        </Tag>

        <Tag
            v-if="shouldShowCurrentBatch"
            severity="info"
            :class="[
              '!px-2 !py-1 text-xs cursor-pointer transition-colors select-none',
              isNavigatingToCurrent
                ? 'opacity-50 cursor-wait'
                : 'hover:bg-blue-200'
            ]"
            @click.stop.prevent="navigateToCurrentBatch"
            @mousedown.stop
            @mouseup.stop
        >
          <Icon
              :icon="isNavigatingToCurrent ? 'mdi:loading' : 'mdi:package-variant'"
              :class="['text-sm me-1', isNavigatingToCurrent ? 'animate-spin' : '']"
          />
          Now in: {{ displayCurrentBatchNumber }}
        </Tag>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
